import { Link } from 'react-router'
import { useT } from '../i18n/useT'
import shotPayrollRun from '../assets/feature-payroll-run.png'
import shotRoe from '../assets/feature-roe.png'
import shotCustomItems from '../assets/feature-custom-items.png'
import shotStatHoliday from '../assets/feature-stat-holiday.png'
import shotCompliance from '../assets/feature-compliance.png'
import shotRemittances from '../assets/feature-remittances.png'
import shotStructure from '../assets/feature-structure.png'
import shotJobCosting from '../assets/feature-job-costing.png'

// Screenshots keyed by highlight index; undefined = placeholder panel.
// wide: true = horizontal card band (width-constrained); default = tall bleed.
// left: true = mirrored bleed, image docked to the left content margin.
const shots: ({ src: string; wide?: boolean; left?: boolean } | undefined)[] = [
  { src: shotPayrollRun },
  { src: shotStatHoliday, left: true },
  { src: shotCompliance },
  { src: shotRemittances, left: true },
  { src: shotRoe },
  { src: shotCustomItems, left: true },
  { src: shotStructure },
  { src: shotJobCosting, left: true },
]

export default function Features() {
  const t = useT()

  return (
    <>
      <section className="bg-gradient-to-b from-brand-50 via-white to-white">
        <div className="mx-auto max-w-7xl px-8 pt-20 pb-16 text-center">
          <h1 className="text-5xl font-extrabold mb-5 max-w-none mx-auto leading-[1.1] whitespace-nowrap">
            {t.features.h}
          </h1>
          <p className="text-lg text-ink-mute max-w-none mx-auto whitespace-nowrap">
            {t.features.sub}
          </p>
        </div>
      </section>

      {t.features.highlights.map((h, i) => {
        const text = (
          <>
            <p className="text-sm font-semibold uppercase tracking-widest text-brand-700 mb-3">
              {h.kicker}
            </p>
            <h2 className="text-3xl font-extrabold mb-4">{h.title}</h2>
            <p className="text-[17px] leading-relaxed text-ink-mute mb-6">{h.body}</p>
            <ul className="space-y-2.5">
              {h.points.map((p) => (
                <li key={p} className="flex items-center gap-3 text-[15px] font-medium text-ink-soft">
                  <span className="w-6 h-6 rounded-full bg-brand-100 grid place-items-center shrink-0">
                    <svg viewBox="0 0 24 24" fill="none" stroke="#15803d" strokeWidth={2.6} className="w-3.5 h-3.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </>
        )

        // Features with a UI screenshot: full-bleed visual docked to the
        // content margin (right by default, left when shot.left), fading
        // on its open edges — never on the edge facing the text.
        const shot = shots[i]
        if (shot) {
          return (
            <section key={h.kicker} className="relative overflow-hidden bg-white">
              <div
                className={`absolute inset-y-0 ${shot.left ? 'left-0' : 'right-0'} ${shot.wide ? 'w-[56%]' : 'w-[70%]'} pointer-events-none`}
                aria-hidden="true"
              >
                <div
                  className={`absolute inset-y-0 flex items-center ${
                    shot.left
                      ? 'right-0 left-[max(2rem,calc((100vw-80rem)/2+2rem))] justify-start'
                      : 'left-0 right-[max(2rem,calc((100vw-80rem)/2+2rem))] justify-end'
                  }`}
                >
                  {shot.wide ? (
                    <div className="relative w-full">
                      <img src={shot.src} alt="" className="w-full h-auto" />
                      <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-white to-transparent" />
                      <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-white to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent" />
                    </div>
                  ) : (
                    <div className="relative h-full flex items-center">
                      <div className="relative">
                        <img src={shot.src} alt="" className="h-auto w-[520px] max-w-full" />
                        {/* fades ride on the image, so the blend holds at any size */}
                        <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-white to-transparent" />
                        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white to-transparent" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="relative mx-auto max-w-7xl px-8 py-36">
                <div className={`max-w-xl ${shot.left ? 'ml-auto' : ''}`}>{text}</div>
              </div>
            </section>
          )
        }

        return (
          <section key={h.kicker} className={i % 2 === 0 ? 'bg-white' : 'bg-mist'}>
            <div className="mx-auto max-w-7xl px-8 py-28 grid grid-cols-12 gap-14 items-center">
              <div className={`col-span-7 ${i % 2 === 1 ? 'order-2' : ''}`}>{text}</div>
              <div className={`col-span-5 ${i % 2 === 1 ? 'order-1' : ''}`}>
                <div className="aspect-[4/3] rounded-2xl bg-gradient-to-br from-brand-100 via-brand-50 to-white border border-brand-100 grid place-items-center">
                  <div className="text-center px-8">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-600 text-white grid place-items-center mb-4 shadow-lg shadow-brand-600/25">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-7 h-7">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4-4 4 3 6-7M4 20h16" />
                      </svg>
                    </div>
                    <p className="text-sm font-medium text-ink-mute">
                      {t.features.placeholder}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">{h.kicker}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )
      })}

      <section className="bg-ink text-white">
        <div className="mx-auto max-w-7xl px-8 py-24 text-center">
          <h2 className="text-3xl font-extrabold text-white mb-3">
            {t.features.includedH}
          </h2>
          <p className="text-slate-400 mb-9">{t.features.includedSub}</p>
          <Link
            to="/pricing"
            className="inline-block rounded-md bg-brand-500 hover:bg-brand-400 transition-colors text-ink font-semibold px-7 py-3.5"
          >
            {t.features.cta}
          </Link>
        </div>
      </section>
    </>
  )
}
